import * as THREE from 'three';
import { DEFAULT_SITE, getGradeBins } from '../config/siteConfig';

export function getDefaultGradeThresholds() {
  const bins = getGradeBins();
  return Array.isArray(bins) && bins.length ? bins : DEFAULT_SITE.gradeBins;
}

/** @deprecated Use getDefaultGradeThresholds() — site.json gradeBins is the source of truth. */
export const DEFAULT_GRADE_THRESHOLDS = DEFAULT_SITE.gradeBins;

export const BLOCK_MODEL_GRADE_PALETTE = Object.freeze({
  selected: 0xfff200,
  get thresholds() {
    return getDefaultGradeThresholds();
  },
});

let liveThresholdsGetter = null;

export function setLiveGradeThresholdsGetter(getter) {
  liveThresholdsGetter = typeof getter === 'function' ? getter : null;
}

export function getLiveGradeThresholds() {
  const live = liveThresholdsGetter?.();
  return Array.isArray(live) && live.length ? live : getDefaultGradeThresholds();
}

export function colorToCss(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '#888888';
  return `#${Math.max(0, Math.min(0xffffff, n)).toString(16).padStart(6, '0')}`;
}

export function cssToColor(hex) {
  const raw = String(hex || '').replace('#', '').trim();
  const n = parseInt(raw, 16);
  return Number.isFinite(n) ? n : 0x888888;
}

function parseThresholdColor(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.min(0xffffff, Math.trunc(value)));
  }
  if (typeof value === 'string') {
    const raw = value.replace(/^#/, '').trim();
    const n = parseInt(raw, 16);
    if (Number.isFinite(n)) return Math.max(0, Math.min(0xffffff, n));
  }
  return NaN;
}

export function normalizeGradeThresholds(list) {
  const fallback = getDefaultGradeThresholds();
  const source = Array.isArray(list) && list.length ? list : fallback;
  let floorColor = fallback[fallback.length - 1].color;
  let floorHidden = false;
  const finite = [];
  const seen = new Set();
  for (let i = 0; i < source.length; i += 1) {
    const row = source[i];
    if (!row || typeof row !== 'object') continue;
    const color = parseThresholdColor(row.color);
    const min = row.min == null || row.min === '' ? NaN : Number(row.min);
    if (!Number.isFinite(color)) continue;
    const hidden = !!row.hidden;
    if (!Number.isFinite(min)) {
      floorColor = color;
      floorHidden = hidden;
      continue;
    }
    if (seen.has(min)) continue;
    seen.add(min);
    finite.push({ min, color, hidden });
  }
  finite.sort((a, b) => b.min - a.min);
  return [...finite, { min: Number.NEGATIVE_INFINITY, color: floorColor, hidden: floorHidden }];
}

export function gradeLegendRows(thresholds) {
  const list = normalizeGradeThresholds(thresholds);
  return list.map((t, i) => {
    const next = list[i - 1];
    let label = 'below';
    if (Number.isFinite(t.min)) {
      label = next ? `${fmtGrade(t.min)}–${fmtGrade(next.min)} g/t` : `≥ ${fmtGrade(t.min)} g/t`;
    } else {
      const above = list[list.length - 2];
      label = above ? `< ${fmtGrade(above.min)} g/t` : 'below';
    }
    return {
      min: t.min,
      color: t.color,
      css: colorToCss(t.color),
      label,
      finite: Number.isFinite(t.min),
      hidden: !!t.hidden,
    };
  });
}

export function fmtGrade(n) {
  if (!Number.isFinite(n)) return '';
  const a = Math.abs(n);
  if (a >= 100) return n.toFixed(1);
  if (a >= 1) return n.toFixed(2);
  return String(Number(n.toFixed(4)));
}

export function getGradeBinIndex(value, thresholds) {
  const list = normalizeGradeThresholds(thresholds);
  const v = Number(value);
  if (!Number.isFinite(v)) return list.length - 1;
  for (let i = 0; i < list.length; i += 1) {
    if (v >= list[i].min) return i;
  }
  return list.length - 1;
}

export function isGradeBinVisible(value, thresholds) {
  const list = normalizeGradeThresholds(thresholds);
  const i = getGradeBinIndex(value, list);
  return !list[i]?.hidden;
}

export function getGradeColorHexFromThresholds(value, thresholds) {
  const list = normalizeGradeThresholds(thresholds);
  const v = Number(value);
  if (!Number.isFinite(v)) return list[list.length - 1].color;
  for (let i = 0; i < list.length; i += 1) {
    if (v >= list[i].min) return list[i].color;
  }
  return list[list.length - 1].color;
}

export function getGradeColorFromThresholds(value, thresholds) {
  return new THREE.Color(getGradeColorHexFromThresholds(value, thresholds));
}

export function getGradeColorHexFromBlockModelPalette(value) {
  return getGradeColorHexFromThresholds(value, getLiveGradeThresholds());
}

export function getGradeColorFromBlockModelPalette(value) {
  return new THREE.Color(getGradeColorHexFromBlockModelPalette(value));
}

const RAMP_STOPS = [
  new THREE.Color(0x0000ff),
  new THREE.Color(0x00ff00),
  new THREE.Color(0xff0000),
  new THREE.Color(0xac02b4),
];

export function getNumericRampColor(value, min, max) {
  const v = Number(value);
  const color = new THREE.Color(0x888888);
  if (!Number.isFinite(v)) return color;
  const lo = Number(min);
  const hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
    return getGradeColorFromBlockModelPalette(v);
  }
  const t = Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
  const scaled = t * (RAMP_STOPS.length - 1);
  const i = Math.min(RAMP_STOPS.length - 2, Math.floor(scaled));
  const frac = scaled - i;
  return color.copy(RAMP_STOPS[i]).lerp(RAMP_STOPS[i + 1], frac);
}
